import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row } from 'react-bootstrap';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useQuery } from '@tanstack/react-query';
import { Shimmer } from 'react-shimmer';
import api from '../services/api';
import debounce from 'lodash/debounce';
import DOMPurify from 'dompurify';
import Pagination from '../components/Pagination';
import { useUpdateHeadTags } from '../hooks/useUpdateHeadTags';
import { useAdverts } from '../hooks/useAdverts';
import AdvertPopup from '../components/AdvertPopup';
import GoogleAdsenseUnit from '../components/GoogleAdsenseUnit';

// Import images
import contactBg from '../assets/images/contact-bg.png';
import editorImg from '../assets/images/editor-blog.png';

//Import SVG Icons
import {BlogBannerSVG , RightArrowSVG} from '../components/SvgIcons';

// Custom hook for fetching blogs
const useBlogs = ({ page, search }) => {
  return useQuery({
    queryKey: ['blogs', page, search],
    queryFn: async () => {
      const response = await api.get('/blogs/get-all-blogs', {
        params: {
          is_mobile: 0,
          is_blog_list: 1,
          page,
          search: search || undefined,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: 3,
  });
};

const Blog = () => {
  const { headers, headersLoading } = useUpdateHeadTags('blogs');
  // Animation
  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  // State for pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');

  // Advert popup logic
  const { data: advertsData } = useAdverts({ position_key: 'blog_popup', ad_format: 'popup', useWebContent: true });
  const [showAdvert, setShowAdvert] = useState(false);
  const [shownOnce, setShownOnce] = useState(false);

  // Debounced search handler
  const debouncedSearch = debounce((value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, 500);

  // Handle search input change
  const handleSearchChange = (e) => {
    let value = e.target.value;
    if (/^[a-zA-Z0-9\s.,!?'-]*$/.test(value)) {
      setInputValue(value);
      const sanitizedValue = DOMPurify.sanitize(value);
      debouncedSearch(sanitizedValue);
    }
  };

  // Fetch blogs
  const { data: blogData, isLoading, isError, error } = useBlogs({
    page: currentPage,
    search: searchQuery,
  });

  // Advert popup display logic
  useEffect(() => {
    if (!isLoading && advertsData?.dataset?.data?.length && !shownOnce) {
      setShowAdvert(true);
      setShownOnce(true);
    }
  }, [isLoading, advertsData, shownOnce]);

  const handleCloseAdvert = () => setShowAdvert(false);
  function getRandomAdvert(advertData) {
    const arr = advertData?.dataset?.data;
    if (Array.isArray(arr) && arr.length > 0) {
      const idx = Math.floor(Math.random() * arr.length);
      return arr[idx];
    }
    return undefined;
  }
  const popupAdvert = getRandomAdvert(advertsData);

  // Pagination handler
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fallback data for banner
  const fallbackBanner = {
    title_badge: 'UPDATES',
    heading: 'Latest blog post',
    description: 'MCQs, Notes, Tests & Lectures for All Competitive Exams',
    banner_navigation: [
      { label: 'Home', url: '/' },
      { label: 'Blog', url: '/blog' },
    ],
  };

  // Map banner content
  const bannerContent = blogData?.dataset?.data
    ? {
      title_badge: blogData.dataset.data.heading_text || fallbackBanner.title_badge,
      heading: blogData.dataset.data.title || fallbackBanner.heading,
      description: DOMPurify.sanitize(blogData.dataset.data.small_description || fallbackBanner.description),
      banner_navigation: fallbackBanner.banner_navigation, // Static, as not provided by API
    }
    : fallbackBanner;
  // Map Editor's Choice
  const editorChoice = blogData?.dataset?.data?.editor_choice
    ? {
      img: blogData.dataset.data.editor_choice.image_url || editorImg,
      category: (blogData.dataset.data.editor_choice.categories?.split(', ')[0]) || 'General',
      title: blogData.dataset.data.editor_choice.title,
      short_description: blogData.dataset.data.editor_choice.short_description,
      author: blogData.dataset.data.editor_choice.author || 'Anonymous',
      created_at: blogData.dataset.data.editor_choice.created_at || 'Unknown Date',
      readMoreLink: `/blog/${blogData.dataset.data.editor_choice.parent_category_slug
          ? `${encodeURIComponent(blogData.dataset.data.editor_choice.parent_category_slug.toLowerCase())}/`
          : ''
        }${encodeURIComponent(
          blogData.dataset.data.editor_choice.child_category_slug.toLowerCase() || 'general'
        )}/${encodeURIComponent(
          blogData.dataset.data.editor_choice.slug || btoa(blogData.dataset.data.editor_choice.blog_id)
        )}`,
    }
    : null;

  // Map Blog List
  const blogList = blogData?.dataset?.data?.blogs_list?.map((blog, index) => ({
    img: blog.image_url || editorImg,
    category: (blog.categories?.split(', ')[0]) || 'General',
    title: blog.title,
    author: blog.author || 'Anonymous',
    created_at: blog.created_at || 'Unknown Date',
    readMoreLink: `/blog/${blog.parent_category_slug ? `${encodeURIComponent(blog.parent_category_slug.toLowerCase())}/` : ''
      }${encodeURIComponent(
        blog.child_category_slug.toLowerCase() || 'general'
      )}/${encodeURIComponent(blog.slug || btoa(blog.blog_id))}`,
  })) || [];

  const totalPages = blogData?.dataset?.data?.pagination?.totalPages || 5;

  return (
    <>
      {/* Banner Section */}
      <section
        className="contact-banner blog-banner">
     <div className="svg-position svg-img blogSVG">
       <BlogBannerSVG/>
<div className="svg-content w-100">   
        <div className="floating-dots">
          {[...Array(10)].map((_, i) => (
            <div className="dot" key={i}></div>
          ))}
        </div>
        <Container>
          <Row className="align-items-center">
            <div className="col-lg-12">
              <div
                className="section_title top_content text-center"
                data-aos="fade-up"
                data-aos-duration="1500"
                data-aos-delay="300"
              >
                {isLoading ? (
                  <>
                    <Shimmer width={100} height={20} className="title_badge mb-1" />
                    <Shimmer width={200} height={30} />
                    <Shimmer width={300} height={20} />
                  </>
                ) : (
                  <>
                    <span className="title_badge mb-1">{bannerContent.title_badge}</span>
                    <h2>{bannerContent.heading}</h2>
                    <p className="mb-0" dangerouslySetInnerHTML={{ __html: bannerContent.description }} />
                    <ul className="banner-nav">
                      {bannerContent.banner_navigation.map((nav, index) => (
                        <React.Fragment key={index}>
                          <li>
                            <Link to={nav.url}>{nav.label}</Link>
                          </li>
                          {index < bannerContent.banner_navigation.length - 1 && (
                            <li>
                              <span>»</span>
                            </li>
                          )}
                        </React.Fragment>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search here"
                  className="form-control"
                  value={inputValue}
                  onChange={handleSearchChange}
                  maxLength={100}
                  pattern="[A-Za-z0-9\s.,!?'-]*"
                  title="Only letters, numbers, spaces, and common punctuation are allowed"
                />
                <i className="fa fa-search"></i>
              </div>
            </div>
          </Row>
        </Container>
         </div>
        </div>
      </section>

      {/* Error Message */}
      {isError && (
        <Container>
          <div className="alert alert-danger text-center">
            Failed to load blogs: {error?.message || 'Please try again later.'}
          </div>
        </Container>
      )}

            {/* Editor Choice Section */}
      {(isLoading || editorChoice) && (
        <section className="editor-choice">
          <Container>
            <Row className="editor-bg">
              <div className="editor-text">
                <div className="editor">
                  Editor <br />
                  Choice
                </div>
              </div>
              <div className="col-lg-6">
                <div className="story-content">
                  <div className="story-img">
                    {isLoading ? (
                      <Shimmer width={300} height={200} />
                    ) : (
                      <img src={editorChoice.img} alt={editorChoice.title} />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="story-content">
                  <div className="story-description">
                    {isLoading ? (
                      <>
                        <Shimmer width={100} height={20} className="span-text" />
                        <Shimmer width={250} height={30} className="story-heading" />
                        <Shimmer width={200} height={20} />
                      </>
                    ) : (
                      <>
                        <span className="span-text">{editorChoice.category}</span>
                        <h6 className="story-heading">
                          <Link to={editorChoice.readMoreLink}>{editorChoice.title}</Link>
                        </h6>
                        <p className="mb-0">{editorChoice.short_description}</p>
                        <div className="story-text">
                          <div className="story-left">
                            {editorChoice.author} | {editorChoice.created_at}
                          </div>
                          <div className="story-right">
                             <Link to={editorChoice.readMoreLink} className="read-btn">
                              Read More <span>
                                <RightArrowSVG/>
                              </span>
                            </Link>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Row>
          </Container>
        </section>
      )}

      {/* Blog List Section */}
      <section className="blog-list">
        <Container>
          <Row>
            {isLoading ? (
              <>
                <div className="col-md-6">
                  <Shimmer width={300} height={120} />
                  <Shimmer width={100} height={20} className="span-text" />
                  <Shimmer width={250} height={30} className="story-heading" />
                  <Shimmer width={120} height={20} />
                </div>
                <div className="col-md-6">
                  <Shimmer width={300} height={120} />
                  <Shimmer width={100} height={20} className="span-text" />
                  <Shimmer width={250} height={30} className="story-heading" />
                  <Shimmer width={120} height={20} />
                </div>
              </>
            ) : blogList.length > 0 ? (
              blogList.map((blog, index) => (
                <div className="col-md-6" key={index}>
                  <div className="story-content">
                    <div className="story-img">
                      <img src={blog.img} alt={blog.title} />
                    </div>
                    <div className="story-description">
                      <span className="span-text">{blog.category}</span>
                      <h6 className="story-heading">
                        <Link to={blog.readMoreLink}>{blog.title}</Link>
                      </h6>
                      <div className="story-text">
                        <div className="story-left">
                          {blog.author} | {blog.created_at}
                        </div>
                        <div className="story-right">
                          <Link to={blog.readMoreLink} className="read-btn">
                            Read More <span>
                              
                              {/* <img src={arrowIcon} alt="" /> */}
                                <RightArrowSVG/>
                              </span>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                <p>No blogs available for this page.</p>
              </div>
            )}
          </Row>
          {!isLoading && blogList.length > 0 && (
            <div className="mt-4">
              <GoogleAdsenseUnit
                className="mx-auto"
                minHeight={140}
                style={{
                  maxWidth: '100%',
                }}
              />
            </div>
          )}
        </Container>
      </section>

      {/* Pagination */}
      {!isError && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      <AdvertPopup
        advert={popupAdvert}
        show={showAdvert}
        onClose={handleCloseAdvert}
        position_key="blog_popup"
        useWebContent={true}
      />
    </>
  );
};

export default Blog;



